'use client';
import { Children, Fragment, isValidElement, useCallback, useContext, useId, useRef, useState, type ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import type { SettingsDocumentProps, SettingsGroupProps, SettingsItemProps, SettingsListProps, SettingsRowProps, SettingsStateProps, SettingsValueProps } from 'elowen-plugin-ui-kit';
import { HelpTip } from './HelpTip';
import { SettingsPresentationContext } from './settingsPresentation';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './shadcn/collapsible';
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from './shadcn/card';
import { Field, FieldContent, FieldDescription, FieldGroup, FieldTitle } from './shadcn/field';
import { Item, ItemActions, ItemContent, ItemDescription, ItemGroup, ItemMedia, ItemTitle } from './shadcn/item';
import { WorkspaceShell } from './WorkspaceShell';
import type { WorkspaceHeroProps } from './WorkspaceHero';
import { usePersistentState } from '../../lib/usePersistentState';
import { SETTINGS_FOLD_EXCEPTIONS } from '../../lib/settingsFoldExceptions.mjs';
import { cn } from '../../lib/utils';

type SettingsDensity = NonNullable<SettingsGroupProps['density']>;

/** Where a remembered fold lives. ONE namespace for every settings and account surface (and the
 *  conversation switcher's pinned section, through `useGroupFold`), so a group is
 *  identified by its own stable id and never by the page that happens to render it. Like every other UI
 *  preference in this app (`elowen.settings.category`, `elowen.memory.tab`, the chat and dock keys) it is
 *  per BROWSER rather than per user id: nothing here scopes a preference by account, and inventing a
 *  second convention for this one switch would be the drift rather than the fix. */
const GROUP_FOLD_PREFIX = 'elowen.settings.fold.';
const FOLD_STATES = ['open', 'closed'] as const;
type FoldState = (typeof FOLD_STATES)[number];

/** Whether this primitive renders inside a drawer — see `SettingsPresentationContext`. */
const useDrawerPresentation = () => useContext(SettingsPresentationContext) === 'drawer';

/** A settings or account page is a STACK OF SECTION CARDS, not one long bordered document. The shell
 *  `control-surface-document` would otherwise draw around the whole stack is dropped in CSS, so each
 *  group carries its own border and the gap between them does the separating. */
export function SettingsDocument({ children, className = '' }: SettingsDocumentProps) {
  return <div data-control-surface data-settings-document className={`control-surface-document settings-document ${className}`}>{children}</div>;
}

/** One section card, built on the shadcn Card (components/ui/shadcn/card.tsx): a header naming it — title,
 *  optional icon, sentence, help and actions — over a body of records. The records are `SettingsRow`s,
 *  which are responsive Fields, so the body is a `FieldGroup`: the container a record asks how wide it is,
 *  and the gap that separates records instead of a rule under each one. A body may hold anything else too
 *  (a register, an error state, a plugin's own panel); it sits in the card's padded content like a record.
 *
 *  `columns={2}` splits the records into two stacks. They stand side by side once the card is wide enough
 *  for two record columns and one above the other before that; the split happens here rather than in CSS
 *  because each stack has to be its own field group.
 *
 *  `collapsible` folds the body under the header, open at first: ONE trigger button covers the heading (the
 *  actions and the help mark stay OUTSIDE it, so neither can toggle the group) and the chevron at its leading
 *  edge states the fold. The body stays MOUNTED while closed — `hidden` on an always-rendered
 *  `CollapsibleContent forceMount` — so deep links and in-page search still find rows that are folded
 *  away, and a caller can force the group open through `open`/`onOpenChange`.
 *
 *  Collapsible sections start open unless listed as exceptions. `storageKey` remembers the reader's choice
 *  across reloads and is the ONE mechanism for that: it wraps the controlled `open`/`onOpenChange` pair. A
 *  collapsible group WITHOUT a `storageKey` is not remembered; controlled per-record items deliberately omit it. */

/** The dispatcher holds NO state of its own, which is what lets the persisted variant be a separate
 *  component and still obey the rules of hooks. */
export function SettingsGroup(props: SettingsGroupProps) {
  if (props.collapsible && props.storageKey && props.open === undefined) {
    return <PersistedSettingsGroup {...props} storageKey={props.storageKey} />;
  }
  return <SettingsGroupView {...props} />;
}

/** The remembered fold of one group, as the `open`/`onOpenChange` pair a controlled group takes. It is the
 *  only place the storage key is built. A caller that must own the fold above something that remounts the
 *  group (AccessRow keeps it above the detail fetch) uses this hook rather than a fold of its own.
 *  `usePersistentState` starts from the fallback and rehydrates inside an effect, so the server and the
 *  first client paint agree and a stored closed group folds a tick later. */
export function useGroupFold(storageKey: string, defaultOpen: boolean): [boolean, (open: boolean) => void] {
  const [stored, setStored] = usePersistentState<FoldState>(`${GROUP_FOLD_PREFIX}${storageKey}`, defaultOpen ? 'open' : 'closed', FOLD_STATES);
  const setOpen = useCallback((next: boolean) => setStored(next ? 'open' : 'closed'), [setStored]);
  return [stored === 'open', setOpen];
}

/** The remembered fold starts open except for the approved keys in SETTINGS_FOLD_EXCEPTIONS.
 *  The existing defaultOpen prop remains available for API 78, but app callers use the shared policy
 *  rather than introducing their own defaults. A deep link into a folded group still works unchanged:
 *  `useRowAnchor` clicks the trigger, the click lands here as `onOpenChange(true)`, and the group both opens
 *  and remembers that it did. */
function PersistedSettingsGroup({ storageKey, defaultOpen = true, onOpenChange, ...rest }: SettingsGroupProps & { storageKey: string }) {
  const exception = SETTINGS_FOLD_EXCEPTIONS.some((entry) => entry.key === storageKey);
  const [open, setOpen] = useGroupFold(storageKey, defaultOpen && !exception);
  const handleOpenChange = useCallback((next: boolean) => {
    setOpen(next);
    onOpenChange?.(next);
  }, [onOpenChange, setOpen]);
  return <SettingsGroupView {...rest} collapsible open={open} onOpenChange={handleOpenChange} />;
}

/** The card's name: the section glyph in the quiet ink (the accent means state in this design, and every
 *  header carrying one made "important" the default), the title as the card's heading, and the help mark. */
function GroupHeading({ title, description, hint, icon: Icon }: Pick<SettingsGroupProps, 'title' | 'description' | 'hint' | 'icon'>) {
  return (
    <>
      {title || Icon ? (
        <CardTitle>
          {Icon ? <Icon size={16} strokeWidth={1.75} aria-hidden className="shrink-0 text-muted-foreground" /> : null}
          {title ? <h2 className="min-w-0">{title}</h2> : null}
          {hint ? <span className="pointer-events-auto inline-flex shrink-0 pointer-coarse:-my-3.5"><HelpTip align="left">{hint}</HelpTip></span> : null}
        </CardTitle>
      ) : null}
      {description ? <CardDescription>{description}</CardDescription> : null}
    </>
  );
}

/** A drawer section's name: the record-sized title (the drawer's own name is the dialog's h2, so a section
 *  is an h3), the help mark, and the description as one muted line under it. */
function SectionHeading({ title, description, hint, icon: Icon }: Pick<SettingsGroupProps, 'title' | 'description' | 'hint' | 'icon'>) {
  return (
    <>
      {title || Icon ? (
        <div className="flex min-w-0 items-center gap-2">
          {Icon ? <Icon size={16} strokeWidth={1.75} aria-hidden className="shrink-0 text-muted-foreground" /> : null}
          {title ? <h3 className="min-w-0 text-section-title tracking-ui text-foreground">{title}</h3> : null}
          {hint ? <span className="pointer-events-auto inline-flex shrink-0 pointer-coarse:-my-3.5"><HelpTip align="left">{hint}</HelpTip></span> : null}
        </div>
      ) : null}
      {description ? <div className="text-meta tracking-ui text-muted-foreground">{description}</div> : null}
    </>
  );
}

/** The fold control of a collapsible group, in both presentations: a native button laid over the heading
 *  with the chevron at its leading edge. The visible heading sits above it with pointer events passed
 *  through, except for help, which stays clickable beside the title outside the button. */
function FoldTrigger({ contentId, title, description }: { contentId: string; title: SettingsGroupProps['title']; description: SettingsGroupProps['description'] }) {
  return (
    <CollapsibleTrigger asChild>
      <button
        type="button"
        aria-controls={contentId}
        className="group/fold absolute inset-0 flex items-start rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
      >
        <ChevronRight
          size={16}
          strokeWidth={1.75}
          aria-hidden
          className="mt-0.5 shrink-0 text-muted-foreground transition-transform duration-[var(--motion-base)] group-hover/fold:text-foreground group-data-[state=open]/fold:rotate-90"
        />
        <span className="sr-only">{title}{title && description ? ' ' : null}{description}</span>
      </button>
    </CollapsibleTrigger>
  );
}

/** The drawer section's body: ONE rounded box (the radius a Settings card has) whose direct children are
 *  its records, each padded alike and separated by air alone, as Settings separates its rows: no hairline
 *  between records (owner, 13 Oct 2026). Rows, lists and states are drawn for it (a list's items are rows
 *  of this same box, so the list itself takes no padding; a state inside the box drops its own border).
 *  Anything else a section holds — a ring grid, an error or loading state, a form around a row — gets a
 *  row's padding, so nothing touches the border. It is the container the rows ask how wide they are. */
function SectionBody({ tone, children }: { tone: NonNullable<SettingsGroupProps['tone']>; children: ReactNode }) {
  return (
    <div
      data-slot="settings-section-body"
      className={cn(
        '@container/settings-section min-w-0 rounded-xl border border-hairline py-2',
        '[&>*]:min-w-0 [&>*]:px-4 [&>*]:py-2.5',
        '[&>[data-slot=item-group]]:p-0',
        '[&>[data-slot=settings-state]]:rounded-none [&>[data-slot=settings-state]]:border-0',
        tone === 'danger' && 'border-destructive/30',
      )}
    >
      {children}
    </div>
  );
}

function SettingsGroupView({ title, description, hint, icon, actions, tone = 'default', density = 'comfortable', columns = 1, rowId, collapsible = false, defaultOpen, open, onOpenChange, forceOpen = false, children, className = '' }: SettingsGroupProps) {
  const drawer = useDrawerPresentation();
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen ?? true);
  // Resolving attention while typing must not hide the focused control. React focus events also
  // include descendant portals, so a required picker stays reachable through its popup.
  const [focusedReveal, setFocusedReveal] = useState(false);
  const focusTransition = useRef(0);
  const isOpen = forceOpen || focusedReveal || (open ?? uncontrolledOpen);
  // Radix's trigger drops `aria-controls` while closed (its content is unmounted); ours is force-mounted
  // and merely `hidden`, so the pair must hold in BOTH states — hand in an id of our own.
  const contentId = useId();
  const setOpen = (next: boolean) => {
    if (forceOpen) return;
    if (!next) setFocusedReveal(false);
    if (open === undefined) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };
  const groupProps = {
    'data-settings-group': '',
    'data-tone': tone,
    'data-density': density,
    'data-row-id': rowId,
    onFocusCapture: () => {
      focusTransition.current++;
      if (forceOpen || focusedReveal) setFocusedReveal(true);
    },
    onBlurCapture: () => {
      const transition = ++focusTransition.current;
      // A blur followed by focus inside the same React subtree is one transfer, including portals.
      queueMicrotask(() => { if (focusTransition.current === transition) setFocusedReveal(false); });
    },
  };

  if (drawer) {
    // A section rather than a card: the heading and the actions stand above ONE box of records. A drawer
    // is one column wide, so `columns` and `density` change nothing here; the box has one rhythm.
    const sectionProps = { ...groupProps, 'data-slot': 'settings-section', 'data-settings-presentation': 'drawer', className: cn('flex min-w-0 flex-col gap-2.5', className) };
    const sectionActions = actions ? <div className="ml-auto flex shrink-0 flex-wrap items-center justify-end gap-2 text-meta text-muted-foreground">{actions}</div> : null;
    const box = children ? <SectionBody tone={tone}>{children}</SectionBody> : null;
    if (!collapsible) {
      return (
        <div {...sectionProps}>
          {title || description || actions ? (
            <div data-slot="settings-section-header" className="flex min-w-0 flex-wrap items-center gap-3">
              <div className="flex min-w-0 flex-auto flex-col gap-0.5"><SectionHeading title={title} description={description} hint={hint} icon={icon} /></div>
              {sectionActions}
            </div>
          ) : null}
          {box}
        </div>
      );
    }
    return (
      <Collapsible open={isOpen} onOpenChange={setOpen} asChild>
        <div {...sectionProps}>
          <div data-slot="settings-section-header" className="flex min-w-0 flex-wrap items-center gap-3">
            <div className="relative min-w-0 flex-auto">
              <FoldTrigger contentId={contentId} title={title} description={description} />
              <div className="pointer-events-none relative flex min-w-0 flex-col gap-0.5 pl-6">
                <SectionHeading title={title} description={description} hint={hint} icon={icon} />
              </div>
            </div>
            {sectionActions}
          </div>
          {box ? <CollapsibleContent forceMount id={contentId} hidden={!isOpen}>{box}</CollapsibleContent> : null}
        </div>
      </Collapsible>
    );
  }

  const cardProps = { ...groupProps, className: cn(tone === 'danger' && 'border-destructive/30', className) };
  const body = children ? <SettingsGroupBody density={density} columns={columns}>{children}</SettingsGroupBody> : null;
  const actionSlot = actions ? <CardAction className="flex flex-wrap items-center gap-2">{actions}</CardAction> : null;

  if (!collapsible) {
    return (
      <Card {...cardProps}>
        {title || description || actions ? (
          <CardHeader>
            <GroupHeading title={title} description={description} hint={hint} icon={icon} />
            {actionSlot}
          </CardHeader>
        ) : null}
        {body}
      </Card>
    );
  }

  return (
    // The root merges its props into the card, and the child's win: restating the card's slot keeps a
    // folding card a `card` to every selector, instead of the collapsible's `data-slot` replacing it.
    <Collapsible open={isOpen} onOpenChange={setOpen} asChild>
      <Card {...cardProps} data-slot="card">
        <CardHeader>
          <div className="relative col-start-1 row-span-2 min-w-0">
            <FoldTrigger contentId={contentId} title={title} description={description} />
            <div className="pointer-events-none relative flex min-w-0 flex-col gap-1.5 pl-6">
              <GroupHeading title={title} description={description} hint={hint} icon={icon} />
            </div>
          </div>
          {actionSlot}
        </CardHeader>
        {/* Mounted even when closed: the body is what deep links and anchors point at, and search has
            to find text inside a folded group. Visibility (not existence) is the only thing that folds. */}
        {body ? <CollapsibleContent forceMount id={contentId} hidden={!isOpen}>{body}</CollapsibleContent> : null}
      </Card>
    </Collapsible>
  );
}

/** The records, in one field group or split into two. The first stack takes the extra record on an odd
 *  count, so a three-row form reads 2 + 1 top-down rather than leaving a gap in the left column. */
function SettingsGroupBody({ density, columns, children }: { density: SettingsDensity; columns: 1 | 2; children: ReactNode }) {
  const gap = density === 'compact' ? 'gap-4' : 'gap-6';
  const items = columns === 2 ? Children.toArray(children) : [];
  if (columns === 1 || items.length < 2) {
    return <CardContent><FieldGroup className={gap}>{children}</FieldGroup></CardContent>;
  }
  const half = Math.ceil(items.length / 2);
  return (
    <CardContent className="@container/columns">
      <div className={cn('grid grid-cols-1 @4xl/columns:grid-cols-2 @4xl/columns:gap-x-10', gap)}>
        <FieldGroup className={gap}>{items.slice(0, half)}</FieldGroup>
        <FieldGroup className={gap}>{items.slice(half)}</FieldGroup>
      </div>
    </CardContent>
  );
}

/** How many trailing actions a record may carry beside its control. Three is the ceiling because the
 *  trailing side is ONE line and icon-only actions are what a record carries (manage / settings /
 *  remove); anything more stops being a record and becomes a toolbar, which belongs in the section
 *  header where there is room for it. */
export const MAX_ROW_ACTIONS = 3;

/** Trailing slots in a node, counting THROUGH fragments. `Children.count` reports `<><A/><B/><C/></>`
 *  as one, which is the shape most call sites hand in, so counting without this would report every
 *  overloaded row as compliant. */
function countSlots(node: ReactNode): number {
  return Children.toArray(node).reduce<number>((total, child) => (
    isValidElement(child) && child.type === Fragment
      ? total + countSlots((child.props as { children?: ReactNode }).children)
      : total + 1
  ), 0);
}

/** The label's last word and the help mark as one unbreakable run: a browser may break a line between text
 *  and the inline mark, which left the mark alone under a long label (Czech "Vynulovat měsíční limit"). */
function LabelWithHelp({ label, hint }: { label: string; hint: string }) {
  const lastWord = label.lastIndexOf(' ') + 1;
  return <>
    {label.slice(0, lastWord)}
    <span className="whitespace-nowrap">
      {label.slice(lastWord)}
      <span className="ml-1.5 inline-flex size-4 items-center justify-center align-[--spacing(-0.75)]"><HelpTip align="left">{hint}</HelpTip></span>
    </span>
  </>;
}

/** A record in the DRAWER presentation: one line of a section's box, on a grid whose areas the row's kind
 *  picks, so every cell keeps its place and only the template moves.
 *
 *  - A property (inline, the default) reads label | value | status and actions: the label in a fixed column
 *    (8rem, 10rem once the box is 28rem wide; the record name of the Settings window), so every label of a box shares one column and every value starts on one line;
 *    the value left-aligned after it; the status and actions muted at the trailing edge. The description
 *    sits under the value, which keeps both columns clean. Below an 18rem box only a wide editor
 *    (slider, input, select or picker) may fold; compact readings stay beside their labels.
 *  - A setting (a record whose control STARTS with a switch, found by the same selector the card row
 *    uses) reads as a setting: its name over the description, the switch at the trailing edge beside both.
 *    A control that is one lone button (Reset usage, a catalog refresh) is NOT a setting: it is a property
 *    whose value is that button, so it starts in the value column under the values of the rows above.
 *  - A stacked record (`trailingLayout="stack"`) names itself on the first line, then the description,
 *    a block status and the control at the box's full width.
 *  - One icon action shares the label/value line, including on phones. Labelled or multiple actions,
 *    and every action of a stacked row, keep their final right-aligned line below the control.
 *
 *  Every cell is at least one control tall (32px) on a wide box, so a line holding text and a line holding
 *  a button or picker are the same height and the text sits where the control's label does. The padding
 *  is the box's, and the `field-*` slots are the card row's, so touch floors and tests find them alike. */
function DrawerRow({ label, description, hint, iconNode, control, status, actions, trailingLayout, rowId, className }: Omit<SettingsRowProps, 'children'> & { trailingLayout: NonNullable<SettingsRowProps['trailingLayout']> }) {
  const stacked = trailingLayout === 'stack';
  const endStatus = stacked ? undefined : status;
  return (
    <div
      role="group"
      data-slot="field"
      data-trailing={trailingLayout}
      data-row-id={rowId}
      data-settings-presentation="drawer"
      className={cn(
        'group/settings-row grid min-w-0 items-start gap-x-2',
        stacked
          ? "grid-cols-[minmax(0,1fr)_auto] [grid-template-areas:'label_label'_'desc_desc'_'status_status'_'value_value'_'actions_actions']"
          : [
            // The NARROW box is the base case, because a container-query variant only ever ADDS a rule: the
            // fold cannot be expressed by one. An editor that cannot shrink — a slider, or a labelled button
            // 180px wide — does not fit beside its label on a 320px phone, so there the label takes the line
            // and the control gets the whole line underneath. Readings and icon actions keep their columns.
            "grid-cols-[auto_minmax(0,1fr)_auto_auto] [grid-template-areas:'label_label_end_action'_'value_value_value_value'_'desc_desc_desc_desc'_'actions_actions_actions_actions']",
            // From a 20rem box up, the value shares the label's line again, and a status keeps its own
            // column. With no status, that empty column (and its inner gap) goes back to the value: otherwise
            // a 390px slider loses track width to a cell that contains nothing.
            endStatus
              ? "@xs/settings-section:[grid-template-areas:'label_value_end_action'_'._desc_desc_desc'_'actions_actions_actions_actions']"
              : "@xs/settings-section:[grid-template-areas:'label_value_value_action'_'._desc_desc_desc'_'actions_actions_actions_actions']",
            '@xs/settings-section:grid-cols-[--spacing(32)_minmax(0,1fr)_auto_auto] @md/settings-section:grid-cols-[--spacing(40)_minmax(0,1fr)_auto_auto]',
            "has-[>[data-slot=field-band]>[data-slot=field-control]>[role=switch]:first-child]:grid-cols-[minmax(0,1fr)_auto_auto_auto]",
            "has-[>[data-slot=field-band]>[data-slot=field-control]>[role=switch]:first-child]:[grid-template-areas:'label_value_end_action'_'desc_desc_desc_desc'_'actions_actions_actions_actions']",
          ],
        className,
      )}
    >
      <div
        data-slot="field-content"
        className={cn(
          '[grid-area:label] flex min-h-6 min-w-0 items-center gap-2',
          'text-card-title text-foreground @xs/settings-section:min-h-8',
          !stacked && 'self-center group-has-[>[data-slot=field-band]>[data-slot=field-control]>[role=switch]:first-child]/settings-row:min-h-6',
        )}
      >
        {iconNode ? <span aria-hidden className="flex size-6 shrink-0 items-center justify-center overflow-hidden rounded-md border border-hairline bg-muted text-muted-foreground">{iconNode}</span> : null}
        {/* Inline text flow, so the help mark follows the label's last word instead of wrapping onto a line
            of its own. Its 16px slot keeps the line one text line tall: on a coarse pointer the mark grows to
            the touch target, which would otherwise push the label below the value beside it. The label itself
            still breaks: on a 390px box its column is 8rem and a long name has to wrap inside it rather than
            run over the value column and sit on top of the help mark's own hit target. */}
        <div data-slot="field-label" className={cn('min-w-0', !stacked && (hint ? 'break-words' : 'truncate'))}>
          {hint ? <LabelWithHelp label={label} hint={hint} /> : label}
        </div>
      </div>
      {control || status || actions ? (
        // The band is the card row's slot for everything after the name. It draws no box of its own here
        // (`contents`), so its cells take their places on the row's grid.
        <div data-slot="field-band" className="contents">
          {control ? (
            <div
              data-slot="field-control"
              className={cn(
                '[grid-area:value] flex min-h-6 min-w-0 flex-wrap items-center gap-2 text-ui text-foreground',
                stacked
                  ? 'mt-2 flex-col items-stretch [&>*]:min-w-0'
                  : [
                    '@xs/settings-section:min-h-8 self-center',
                    // A borderless (ghost) button that opens the value draws no edge, so its icon or label is
                    // what has to start on the value line, not its padding: pull it back by its inset. Its
                    // hover wash then bleeds into the column gap. An icon-only one gets an 8px inset of its
                    // own (any call-site box size would otherwise set the inset), the touch target's on a
                    // coarse pointer, where its box is that target and the 14px icon sits in its middle.
                    '[&>[data-slot=button][data-variant^=ghost][data-size=sm]:first-child]:-ms-3.25',
                    '[&>[data-slot=button][data-variant^=ghost][data-size=default]:first-child]:-ms-3.75',
                    '[&>[data-slot=button][data-variant^=ghost][data-size=icon]:first-child]:w-auto [&>[data-slot=button][data-variant^=ghost][data-size=icon]:first-child]:px-2 [&>[data-slot=button][data-variant^=ghost][data-size=icon]:first-child]:-ms-2.25',
                    'pointer-coarse:[&>[data-slot=button][data-variant^=ghost][data-size=icon]:first-child]:-ms-[calc((var(--touch-target)-var(--spacing)*3.5)/2)]',
                  ],
              )}
            >
              {control}
            </div>
          ) : null}
          {stacked && status ? <div data-slot="field-status" className="[grid-area:status] mt-2 min-w-0 text-meta text-muted-foreground">{status}</div> : null}
          {endStatus ? (
            <div className="[grid-area:end] flex min-h-6 min-w-0 items-center justify-end justify-self-end self-center text-meta tabular-nums text-muted-foreground @xs/settings-section:min-h-8">
              <span data-slot="field-status" className="shrink-0 whitespace-nowrap">{endStatus}</span>
            </div>
          ) : null}
          {actions ? <div data-slot="field-actions" className={cn(
            '[grid-area:actions] mt-2 flex min-w-0 max-w-full flex-wrap items-center justify-end gap-2 [&>button]:max-w-full [&>button]:whitespace-normal',
            !stacked && [
              'has-[>[data-action-menu=icon]:only-child]:[grid-area:action] has-[>[data-action-menu=icon]:only-child]:mt-0 has-[>[data-action-menu=icon]:only-child]:self-center',
              'has-[>button[data-size^=icon]:only-child]:[grid-area:action] has-[>button[data-size^=icon]:only-child]:mt-0 has-[>button[data-size^=icon]:only-child]:self-center',
            ],
          )}>{actions}</div> : null}
        </div>
      ) : null}
      {description ? <p data-slot="field-description" className="[grid-area:desc] mt-0.5 min-w-0 text-meta tracking-ui text-muted-foreground">{description}</p> : null}
    </div>
  );
}

/** A label/control record inside a section card, and the canonical anatomy every settings and account
 *  row is built from: a responsive shadcn Field (components/ui/shadcn/field.tsx).
 *
 *  THE CONTRACT. A record is a label and ONE control, optionally a SHORT status and at most
 *  {@link MAX_ROW_ACTIONS} actions. Its name and its `description` read on the left; the control and the
 *  actions share ONE trailing band that takes the field's control column beside them on a wide card and
 *  the full width under them on a narrow one. The band holds the control first and the actions after it,
 *  with the actions on their own full-width line below every control, right-aligned at every width.
 *  Secondary actions use a small ghost button with an icon and label.
 *
 *  WHERE THE STATUS READS. An inline record's status is one SHORT reading about the setting — a state
 *  pill, a count, the model a role resolves to. It belongs to the label: it renders right after the help
 *  mark, on the label's own line. A STACKED record (`trailingLayout="stack"`) is the exception, because
 *  its status is a BLOCK — a provider's endpoint over a model count over a badge row — that cannot sit on
 *  the label's baseline; its band stacks the status, the control and the actions, each at the band's
 *  full width.
 *
 *  `description` is the record's short meaning and reads under its name. `hint` is long-form or
 *  cautionary detail and stays behind the shared HelpTip beside the name.
 *
 *  Inside a drawer (`SettingsPresentationContext`) the same props draw a line of the section's box
 *  instead; see `DrawerRow`. */
export function SettingsRow({ label, description, hint, iconNode, control, status, actions, trailingLayout = 'inline', rowId, children, className = '' }: SettingsRowProps) {
  const drawer = useDrawerPresentation();
  const controlNode = control ?? children;
  // ONE decision about where the status reads, taken from the layout the row already declares — see the
  // contract above. An inline status joins the label line; a stacked one leads the trailing band.
  const stacked = trailingLayout === 'stack';
  const labelStatus = stacked ? undefined : status;
  const bandStatus = stacked ? status : undefined;
  if (process.env.NODE_ENV !== 'production' && countSlots(actions) > MAX_ROW_ACTIONS) {
    // eslint-disable-next-line no-console
    console.warn(`SettingsRow "${label}" carries more than ${MAX_ROW_ACTIONS} actions; move the extras into the section header.`);
  }
  if (drawer) return <DrawerRow {...{ label, description, hint, iconNode, control: controlNode, status, actions, trailingLayout, rowId, className }} />;
  const name = (
    <FieldContent>
      <FieldTitle>
        <span className="min-w-0">{label}</span>
        {hint ? <HelpTip align="left">{hint}</HelpTip> : null}
        {labelStatus ? <span data-slot="field-status" className="min-w-0 max-w-full text-meta text-muted-foreground">{labelStatus}</span> : null}
      </FieldTitle>
      {description ? <FieldDescription>{description}</FieldDescription> : null}
    </FieldContent>
  );
  // A stacked record is a name over a full-width band at every width. `responsive` would put the band beside
  // the name from the card's `@md` width on, in an 18rem column, which is the wrong place for a block control
  // such as a summary card. An action-only record likewise puts its band below the name, so replacing
  // a stored secret or opening related settings never sits beside the status as if it were a value.
  return (
    <Field
      orientation={stacked || (!controlNode && actions) ? 'vertical' : 'responsive'}
      data-trailing={trailingLayout}
      data-row-id={rowId}
      className={cn(
        // A SWITCH STAYS BESIDE ITS NAME at every width. Everything else folds under the name on a narrow
        // card, but a lone switch is the one control whose place beside its label IS the convention, and
        // under it on a phone it read as a stray toggle. Only a band whose control starts with a switch
        // and carries nothing else qualifies, so a record adding a status or an action still folds.
        'has-[>[data-slot=field-band]>:only-child>[role=switch]:first-child]:flex-row',
        'has-[>[data-slot=field-band]>:only-child>[role=switch]:first-child]:items-start',
        'has-[>[data-slot=field-band]>:only-child>[role=switch]:first-child]:[&>[data-slot=field-band]]:w-auto',
        className,
      )}
    >
      {iconNode ? (
        <div data-slot="field-content" className="flex min-w-0 flex-1 items-start gap-3">
          <span aria-hidden className="flex size-7 shrink-0 items-center justify-center overflow-hidden rounded-md border border-hairline bg-muted text-muted-foreground">{iconNode}</span>
          {name}
        </div>
      ) : name}
      {bandStatus || controlNode || actions ? (
        <div
          data-slot="field-band"
          className={cn(
            // Segmented owns its single-line scroll track; the record only bounds its available width.
            'flex min-w-0 gap-2',
            // On a narrow card a text button whose label runs long (Czech "Upravit běhové prostředí"), as
            // the control or as an action, wraps inside itself rather than drawing past the card's edge.
            '@max-md/field-group:[&>*>button:not([data-row-picker],[role=combobox])]:h-auto @max-md/field-group:[&>*>button:not([data-row-picker],[role=combobox])]:max-w-full @max-md/field-group:[&>*>button:not([data-row-picker],[role=combobox])]:py-1.5 @max-md/field-group:[&>*>button:not([data-row-picker],[role=combobox])]:text-start @max-md/field-group:[&>*>button:not([data-row-picker],[role=combobox])]:whitespace-normal',
            '@md/field-group:has-[.segmented]:w-auto! @md/field-group:has-[.segmented]:max-w-[70%]',
            stacked
              ? 'flex-col items-stretch [&>*]:min-w-0'
              : 'flex-wrap items-center justify-end [&>[data-slot=field-actions]]:basis-full',
          )}
        >
          {bandStatus ? <div data-slot="field-status" className="text-meta text-muted-foreground">{bandStatus}</div> : null}
          {controlNode ? (
            // Visible value inputs keep a readable floor; Radix's hidden form checkbox must not size a switch.
            // Every secondary action takes a separate line below the control.
            <div data-slot="field-control" className={cn('flex min-w-0 items-center gap-2 has-[[role=combobox],[data-row-picker],input:not([type=checkbox],[type=radio],[type=hidden]),select]:min-w-36', stacked ? '' : 'flex-1 justify-start @md/field-group:justify-end')}>
              {controlNode}
            </div>
          ) : null}
          {actions ? <div data-slot="field-actions" className="flex min-w-0 max-w-full shrink-0 flex-wrap items-center justify-end gap-2">{actions}</div> : null}
        </div>
      ) : null}
    </Field>
  );
}

/** A loading, empty or error message standing where a card's records would be: a bordered box flush with
 *  the card. It used to borrow the register's state chrome, which Studio paints on the page ground, so in
 *  studio-oled it read as a black hole cut into the card.
 *
 *  In a drawer it is a quiet line instead: one record's height, left-aligned, at the window's size. Inside a
 *  section's box it is a line of that box; on its own (a panel stating that it has nothing to show) its
 *  border makes it a box of one line. */
export function SettingsState({ children, tone = 'default' }: SettingsStateProps) {
  const drawer = useDrawerPresentation();
  return (
    <div
      data-slot="settings-state"
      data-tone={tone}
      data-settings-presentation={drawer ? 'drawer' : undefined}
      className={drawer
        ? 'flex min-h-12 min-w-0 items-center rounded-xl border border-hairline px-4 py-2 text-ui text-muted-foreground data-[tone=danger]:text-destructive'
        : 'rounded-xl border border-hairline bg-raised px-4 py-8 text-center text-ui text-muted-foreground data-[tone=danger]:text-destructive'}
    >
      {drawer ? <div className="min-w-0">{children}</div> : children}
    </div>
  );
}

/** A read-only value standing in a `SettingsRow` control: the row's value size, wrapping rather than
 *  running past the card, right-aligned beside the label on a wide card (left-aligned after the label
 *  column in a drawer), and an absent value is the muted em dash instead of a gap. `mono` is for
 *  identifiers, paths and hashes. It is what every detail window uses where Settings would show an
 *  editable control, so a fact and a setting read as one family. */
export function SettingsValue({ children, mono = false, className }: SettingsValueProps) {
  const drawer = useDrawerPresentation();
  const empty = children === undefined || children === null || children === false || children === '';
  return (
    <span
      data-slot="settings-value"
      className={cn(
        'min-w-0 max-w-full break-words text-ui tabular-nums text-foreground',
        !drawer && '@md/field-group:text-right',
        mono && 'break-all font-mono text-small',
        empty && 'text-muted-foreground',
        className,
      )}
    >
      {empty ? '\u2014' : children}
    </span>
  );
}

/** The register a section card holds when its records are a list of things (people, projects, commits,
 *  snapshots, ports) rather than label/control rows: the same `ItemGroup` Settings draws its provider and
 *  account lists with, so a list in a detail window and a list in Settings are one component. */
export function SettingsList({ children, className }: SettingsListProps) {
  // In a drawer the items are the box's own rows, set apart by the rows' own padding.
  const drawer = useDrawerPresentation();
  return <ItemGroup className={cn(drawer && 'gap-0', className)}>{children}</ItemGroup>;
}

/** One record of a `SettingsList`: an outlined `Item` with the leading mark, the name over a muted line,
 *  one line of muted trailing text and the trailing actions. The name and the line truncate instead of
 *  wrapping, so a row is one height in a list of commits and in a list of people. Trailing actions stay
 *  beside the shrinking name on narrow cards. An `onClick` makes the
 *  entire item a keyboard-accessible button using the selectable Item variant; it cannot contain actions. */
export function SettingsItem({ appearance = 'default', media, title, description, end, actions, onClick, className }: SettingsItemProps) {
  // In a drawer an item is a line of the section's box, not a box of its own: unframed, one record's
  // height, with no divider, as every row of the box.
  const drawer = useDrawerPresentation();
  if (onClick && actions) throw new Error('A clickable SettingsItem cannot contain actions');
  const content = <>
    {media ? <ItemMedia>{media}</ItemMedia> : null}
    <ItemContent className="min-w-0 flex-1">
      <ItemTitle className="w-full"><span className="min-w-0 truncate" title={typeof title === 'string' ? title : undefined}>{title}</span></ItemTitle>
      {description ? <ItemDescription className="truncate">{description}</ItemDescription> : null}
    </ItemContent>
    {end ? <span className="ml-auto shrink-0 whitespace-nowrap text-meta tabular-nums text-muted-foreground">{end}</span> : null}
    {actions ? <ItemActions className={cn('shrink-0 flex-nowrap', !end && 'ml-auto')}>{actions}</ItemActions> : null}
  </>;
  const itemClass = cn('flex-nowrap', drawer && 'min-h-12 rounded-none border-0 bg-transparent px-4 py-2.5 hover:bg-accent/40', !drawer && appearance === 'inline' && 'border-0 bg-transparent hover:bg-muted', className);
  if (onClick) return <div role="listitem">
    <Item asChild variant="selectable" size="sm" role="button" data-settings-presentation={drawer ? 'drawer' : undefined} className={cn('w-full text-left', itemClass)}>
      <button type="button" onClick={onClick}>{content}</button>
    </Item>
  </div>;
  return <Item variant={drawer || appearance === 'inline' ? 'default' : 'outline'} size="sm" data-settings-presentation={drawer ? 'drawer' : undefined} className={itemClass}>{content}</Item>;
}

/** ONE SECTION OF A SETTINGS-STYLE WINDOW, exactly as Settings draws one: the deck-variant workspace shell
 *  with the section's hero (its name as the page title, one sentence, the section's actions and save
 *  status) over a `SettingsDocument` stack of `SettingsGroup` cards. Every window opened on a
 *  `PageOverlay` + `SectionDeck` (the account defaults and presets windows) renders each
 *  of its sections through this, so the window's content is the Settings content with other facts in it
 *  rather than a lookalike. The `eyebrow` names the window, as Settings' names itself. */
export function SettingsPage({ eyebrow, title, description, status, action, children }: Pick<WorkspaceHeroProps, 'eyebrow' | 'description' | 'status' | 'action'> & { title: string; children: ReactNode }) {
  return (
    <WorkspaceShell variant="deck" hero={{ eyebrow, title, description, status, action }}>
      <SettingsDocument>{children}</SettingsDocument>
    </WorkspaceShell>
  );
}
